import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL + '/api/v1';
const OPINION_API_URL = process.env.REACT_APP_OPINION_API_URL || 'http://localhost:8002';

// 创建 axios 实例
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token 管理
const tokenManager = {
  getAccessToken: () => localStorage.getItem('access_token'),
  getRefreshToken: () => localStorage.getItem('refresh_token'),
  setTokens: (access, refresh) => {
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
  },
  clearTokens: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },
  getUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
  setUser: (user) => {
    localStorage.setItem('user', JSON.stringify(user));
  },
};

// 请求拦截器 - 添加 token
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器 - 处理 token 刷新
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 如果是 401 错误且不是刷新 token 的请求
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access, refresh } = response.data;
          tokenManager.setTokens(access, refresh || refreshToken);

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (refreshError) {
          tokenManager.clearTokens();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        tokenManager.clearTokens();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// 认证 API
export const authAPI = {
  login: (username, password) =>
    api.post('/auth/login/', { username, password }),

  logout: () => {
    const refreshToken = tokenManager.getRefreshToken();
    return api.post('/auth/logout/', { refresh: refreshToken });
  },

  refreshToken: () =>
    api.post('/auth/token/refresh/', { refresh: tokenManager.getRefreshToken() }),

  getProfile: () => api.get('/auth/profile/'),

  updateProfile: (data) => api.patch('/auth/profile/', data),

  changePassword: (oldPassword, newPassword, newPasswordConfirm) =>
    api.post('/auth/password/change/', {
      old_password: oldPassword,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    }),

  requestPasswordReset: (email) =>
    api.post('/auth/password/reset/request/', { email }),

  confirmPasswordReset: (email, code, newPassword, newPasswordConfirm) =>
    api.post('/auth/password/reset/confirm/', {
      email,
      code,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    }),

  // 用户注册
  requestRegisterCode: (email) =>
    api.post('/auth/register/request/', { email }),

  confirmRegister: (data) =>
    api.post('/auth/register/confirm/', data),
};

// 用户管理 API
export const userAPI = {
  list: (params) => api.get('/users/', { params }),
  get: (id) => api.get(`/users/${id}/`),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.patch(`/users/${id}/`, data),
  delete: (id) => api.delete(`/users/${id}/`),
  lock: (id, reason) => api.post(`/users/${id}/lock/`, { reason }),
  unlock: (id) => api.post(`/users/${id}/unlock/`),
  resetPassword: (id, newPassword) =>
    api.post(`/users/${id}/reset-password/`, { new_password: newPassword }),
};

// 部门管理 API
export const departmentAPI = {
  list: (params) => api.get('/departments/', { params }),
  tree: () => api.get('/departments/tree/'),
  get: (id) => api.get(`/departments/${id}/`),
  create: (data) => api.post('/departments/', data),
  update: (id, data) => api.patch(`/departments/${id}/`, data),
  delete: (id) => api.delete(`/departments/${id}/`),
};

// 角色管理 API
export const roleAPI = {
  list: (params) => api.get('/roles/', { params }),
  get: (id) => api.get(`/roles/${id}/`),
  create: (data) => api.post('/roles/', data),
  update: (id, data) => api.patch(`/roles/${id}/`, data),
  delete: (id) => api.delete(`/roles/${id}/`),
};

// 权限管理 API
export const permissionAPI = {
  list: (params) => api.get('/permissions/', { params }),
  modules: () => api.get('/permissions/modules/'),
  get: (id) => api.get(`/permissions/${id}/`),
  create: (data) => api.post('/permissions/', data),
  update: (id, data) => api.patch(`/permissions/${id}/`, data),
  delete: (id) => api.delete(`/permissions/${id}/`),
};

// 登录日志 API
export const loginLogAPI = {
  list: (params) => api.get('/login-logs/', { params }),
  get: (id) => api.get(`/login-logs/${id}/`),
};

// 操作记录 API（仅超级管理员）
export const operationLogAPI = {
  list: (params) => api.get('/operation-logs/', { params }),
  get: (id) => api.get(`/operation-logs/${id}/`),
};

// 意见 API
// 创建 axios 实例用于意见 API
const opinionAPI = axios.create({
  baseURL: OPINION_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 意见 API 请求拦截器 - 添加 token
opinionAPI.interceptors.request.use(
  (config) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 意见 API 服务
export const opinionAPIService = {
  submit: (formData) => {
    return opinionAPI.post('/api/opinions', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  list: (params) => opinionAPI.get('/api/opinions', { params }),
  get: (id) => opinionAPI.get(`/api/opinions/${id}`),
  update: (id, data) => opinionAPI.patch(`/api/opinions/${id}`, data),
  getImage: (id, index) => opinionAPI.get(`/api/opinions/${id}/images/${index}`, {
    responseType: 'blob',
  }),
};

export { tokenManager };
export default api;

